import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { ArrowRight, MapPin, Truck, Star } from "lucide-react";

const SITE = SITE_URL;

const areas = [
  { name: "Walt Disney World Resorts", link: "/delivery/walt-disney-world-resorts", desc: "All Disney resort hotels with in-person lobby service." },
  { name: "Universal Orlando", link: "/delivery/universal-orlando", desc: "Universal resort hotels and on-site properties." },
  { name: "Kissimmee & Vacation Homes", link: "/delivery/vacation-homes", desc: "Airbnb, VRBO, vacation rentals and private homes." },
  { name: "Disney Springs Area Hotels", link: "/delivery", desc: "Hotels within the Disney Springs resort area." },
  { name: "Bonnet Creek & Reunion", link: "/delivery", desc: "Vacation clubs and resort properties near WDW." },
  { name: "SeaWorld & I-Drive", link: "/delivery", desc: "Properties near SeaWorld and International Drive." },
];

const faqs = [
  {
    q: "Do you deliver strollers to all Orlando-area hotels?",
    a: "We serve the greater Orlando area including Walt Disney World, Universal Orlando, Kissimmee, Lake Buena Vista, Celebration, Reunion, Bonnet Creek, Disney Springs-area hotels and vacation rentals. Contact us if you are unsure whether your property is within our service area.",
  },
  {
    q: "Is stroller delivery free in Orlando?",
    a: "Standard delivery is free throughout our service area. We offer 1-hour delivery windows from 7 am to 10 pm — no early-delivery surcharges.",
  },
  {
    q: "What strollers do you offer for Orlando rentals?",
    a: "We carry single and double strollers from Baby Jogger, BOB, Nuna, UPPAbaby and Zoe — all sanitized between rentals. Browse our full catalog to compare models and pricing.",
  },
  {
    q: "Can I book a stroller rental for just one day?",
    a: "Yes. Our pricing starts from day one with no minimum rental period beyond the booking dates you choose.",
  },
  {
    q: "How do I choose between a single and double stroller for Orlando?",
    a: "If you have one child under stroller age, a single stroller is typically easier to navigate. If you have two children or one older child who tires quickly, a double stroller gives you more flexibility. Our stroller quiz on the website can also help.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Strollers", item: `${SITE}/strollers` },
    { "@type": "ListItem", position: 3, name: "Orlando Stroller Rentals", item: `${SITE}/stroller-rentals-orlando` },
  ],
};

export default function StrollerRentalsOrlando() {
  return (
    <>
      <PageSEO
        title="Orlando Stroller Rentals | Delivered to Your Hotel or Home"
        description="Rent a premium stroller anywhere in the Orlando area. Free delivery to Disney resorts, Universal Orlando, Kissimmee vacation homes and area hotels. From $20/day."
        canonical={`${SITE}/stroller-rentals-orlando`}
        structuredData={[breadcrumbSchema, faqSchema]}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>
        <section className="pt-28 pb-16 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/strollers" className="hover:text-[#FFD166]">Strollers</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Orlando Stroller Rentals</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">Orlando Stroller Rentals</h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              Premium stroller rentals delivered throughout the greater Orlando area — Walt Disney World, Universal Orlando, Kissimmee, vacation homes and area hotels. Enchanted Gear Rentals is a family-owned, Orlando-based rental service formerly known as Enchanted Baby Gear Rentals.
            </p>
            <div className="mt-7 flex gap-4 justify-center flex-wrap">
              <Link to="/#trip-setup"><button className="btn-primary" data-testid="orlando-cta-top">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
              <Link to="/strollers"><button className="btn-ghost">Browse Strollers</button></Link>
            </div>
          </div>
        </section>

        {/* Service area */}
        <section className="container py-12">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-3">Where we deliver in Orlando</h2>
          <p className="text-[#B9C8D5] mb-8">Our service area covers the major Orlando-area destinations families visit. Free standard delivery included.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {areas.map((a) => (
              <Link key={a.name} to={a.link} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 p-5 transition-all">
                <MapPin className="w-5 h-5 text-[#FFD166] mb-2" />
                <div className="font-serif text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors">{a.name}</div>
                <div className="text-sm text-[#B9C8D5] mt-1">{a.desc}</div>
              </Link>
            ))}
          </div>
        </section>

        {/* Why choose us */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Why Orlando families choose Enchanted Gear Rentals</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              {[
                { Icon: Truck, title: "Free delivery across Orlando", body: "Standard delivery is free. We come to you — at your resort, hotel or vacation home — so you never have to lug a stroller through baggage claim." },
                { Icon: Star, title: "Premium name-brand strollers", body: "Baby Jogger, BOB, Nuna, UPPAbaby and Zoe. Every model is inspected and hospital-grade sanitized before your rental begins." },
                { Icon: MapPin, title: "Family-owned and local", body: "Enchanted Gear Rentals is the same Orlando-area family-owned business formerly known as Enchanted Baby Gear Rentals. We know this area and take every rental personally." },
              ].map(({ Icon, title, body }) => (
                <div key={title} className="flex gap-4">
                  <Icon className="w-6 h-6 text-[#FFD166] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-[#F6F1E4]">{title}</div>
                    <div className="text-sm text-[#B9C8D5] mt-1">{body}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-6">
              <h3 className="font-serif text-lg text-[#FFD166] mb-4">Pricing overview</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between border-b border-[#FFD166]/10 pb-2"><span className="text-[#B9C8D5]">Single strollers from</span><span className="text-[#F6F1E4] font-semibold">$20/day</span></div>
                <div className="flex justify-between border-b border-[#FFD166]/10 pb-2"><span className="text-[#B9C8D5]">Double strollers from</span><span className="text-[#F6F1E4] font-semibold">$28/day</span></div>
                <div className="flex justify-between border-b border-[#FFD166]/10 pb-2"><span className="text-[#B9C8D5]">Standard delivery</span><span className="text-[#F6F1E4] font-semibold">Free</span></div>
                <div className="flex justify-between border-b border-[#FFD166]/10 pb-2"><span className="text-[#B9C8D5]">Early delivery (before 8 am)</span><span className="text-[#F6F1E4] font-semibold">+$25</span></div>
                <div className="flex justify-between"><span className="text-[#B9C8D5]">Multi-day discount</span><span className="text-[#F6F1E4] font-semibold">Automatic</span></div>
              </div>
              <p className="text-xs text-[#B9C8D5] mt-4">Prices shown are starting rates. Select your dates for exact pricing.</p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Frequently asked questions</h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <summary className="font-semibold text-[#F6F1E4] cursor-pointer list-none flex justify-between items-center gap-3">
                  {f.q}
                  <span className="text-[#FFD166] text-xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[#B9C8D5] text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Plan your Orlando stroller rental</h2>
            <p className="text-[#B9C8D5] mb-6">Enter your dates and delivery address to see availability and confirm your booking in seconds.</p>
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="orlando-cta-bottom">Reserve Now <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
